<script setup lang="ts">
import { computed, inject, ref, watch } from 'vue';
import { NxButton } from '../Button';
import { NxDialog } from '../Dialog';
import { NxTextInput } from '../Inputs';
import { i18nInjectionKey } from '../internal';
import type { NxSavedTableFilter } from './filterModel';

/** Name prompt for saving the current filter combination as a named set. */
const isOpen = defineModel<boolean>('open', { required: true });
const props = defineProps<{ existing: NxSavedTableFilter[] }>();
const emit = defineEmits<{ save: [name: string] }>();

const i18n = inject(i18nInjectionKey)!;

const name = ref('');
watch(isOpen, (open) => {
  if (open) name.value = '';
});

const trimmed = computed(() => name.value.trim());
const overwrites = computed(() => props.existing.some((f) => f.name === trimmed.value));

function submit(close?: () => void) {
  if (!trimmed.value) return;
  emit('save', trimmed.value);
  if (close) close();
  else isOpen.value = false;
}
</script>

<template>
  <NxDialog
    v-model:open="isOpen"
    :title="i18n.t('common.table.filter.saved.save_title')"
    :description="i18n.t('common.table.filter.saved.save_description')"
    :resizable="false"
  >
    <template #content>
      <div class="save-dialog-body">
        <NxTextInput
          v-model="name"
          :placeholder="i18n.t('common.table.filter.saved.name_placeholder')"
          height="s"
          @keydown.enter="submit()"
        />
        <p v-if="overwrites" class="overwrite-hint">
          {{ i18n.t('common.table.filter.saved.overwrite_hint') }}
        </p>
      </div>
    </template>
    <template #buttons="{ close }">
      <NxButton :disabled="!trimmed" @click="() => submit(close)">
        {{ i18n.t('common.table.filter.saved.save') }}
      </NxButton>
      <NxButton variant="outline" @click="close">
        {{ i18n.t('common.table.filter.saved.cancel') }}
      </NxButton>
    </template>
  </NxDialog>
</template>

<style scoped>
.save-dialog-body {
  width: min(var(--nxt-panel-width-xl), 92vw);
  margin-top: var(--nxt-space-2xs);
  display: flex;
  flex-direction: column;
  gap: var(--nxt-space-2xs);
}
.overwrite-hint {
  margin: 0;
  font-size: var(--nxt-font-size-sm);
  color: var(--nxt-fg-muted);
}
</style>
